import React from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { RequiredDocument } from '../../data/types/RequiredDocument'
import { Constants, Enum } from '../../data/types/constants'

type Props = {
  applicant: string
  requiredDocument: RequiredDocument[]
  uploadDocument: (file: File[], requiredDocument: RequiredDocument) => void
  currentUploads: string[]
}

function RequiredDocumentComponent({ requiredDocument, applicant, uploadDocument, currentUploads }: Props) {
  const { t } = useTranslation()

  const isSubmited = (status: string) =>
    status === Enum.EDocumentStatus.Approved || status === Enum.EDocumentStatus.AwaitingApproval

  const getUploadButton = (status: string, id: string) => {
    const uploadSpinner = (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <div className="mini-spinner-white" />
        <span style={{ color: 'white', margin: '1rem' }}>{t<string>('uploadRequiredFile.charge')}</span>
      </span>
    )
    const uploadArrowIcon = (
      <span style={{ cursor: 'pointer', color: 'white', padding: '6px' }}>
        <i className="fa-solid fa-arrow-up" />
        <span style={{ color: 'white', margin: '1rem' }}>{t<string>('uploadRequiredFile.charge')}</span>
      </span>
    )
    const approvedIcon = (
      <span
        style={{
          color: 'white',
          padding: '5px',
        }}
      >
        <i
          className="fa-sharp fa-solid fa-check"
          style={{
            color: 'white',
            backgroundColor: '#7BD163',
            borderRadius: '50px',
            padding: '3px',
          }}
        />
        <span style={{ color: 'white', margin: '1rem' }}>{t<string>('uploadRequiredFile.charge')}</span>
      </span>
    )
    if (currentUploads.includes(id) && status !== Enum.EDocumentStatus.Approved) {
      return uploadSpinner
    }

    if (
      status === Enum.EDocumentStatus.AwaitingApproval ||
      status === Enum.EDocumentStatus.Incomplete ||
      !isSubmited(status)
    )
      return uploadArrowIcon
    return approvedIcon
  }
  const getStatusClass = (status: string) => {
    const statusClassMap = {
      [Enum.EDocumentStatus.AwaitingDocument]: 'text-statusBlue',
      [Enum.EDocumentStatus.AwaitingScan]: 'text-statusYellow',
      [Enum.EDocumentStatus.AwaitingApproval]: 'text-statusYellow',
      [Enum.EDocumentStatus.Incomplete]: 'text-statusYellow',
      [Enum.EDocumentStatus.Deleted]: 'texte-statusRed',
      [Enum.EDocumentStatus.Refused]: 'text-statusRed',
      [Enum.EDocumentStatus.Approved]: 'text-statusGreen',
    }

    return statusClassMap[status] || ''
  }

  const hasRefusedDocument = requiredDocument.some((doc) => doc.status === Enum.EDocumentStatus.Refused)

  const hasIncompletedDocument = requiredDocument.some((doc) => doc.status === Enum.EDocumentStatus.Incomplete)

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, doc: RequiredDocument) => {
    const { files } = event.target
    if (files) {
      uploadDocument(Array.from(files), doc)
    }
  }
  return (
    <>
      <h3> {applicant && `${applicant.charAt(0).toUpperCase()}${applicant.slice(1)}`}</h3>
      <div className="scroll-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th>{t('uploadRequiredFile.documentName')}</th>
              {(hasRefusedDocument || hasIncompletedDocument) && <th>{t<string>('uploadRequiredFile.note')}</th>}
              <th>{t<string>('uploadRequiredFile.status')}</th>
              <th>{t('uploadRequiredFile.upload')}</th>
            </tr>
          </thead>
          <tbody>
            {requiredDocument.map((doc) => (
              <tr key={doc.id}>
                <td data-label={t('common.document')}>
                  {i18n.language === 'fr' ? doc.nameFr : doc.nameEn}
                  {doc.note ?? ''}
                </td>
                {(hasRefusedDocument || hasIncompletedDocument) &&
                  (window.innerWidth > 600 ? (
                    <td data-label={t('uploadRequiredFile.note')}>
                      <span style={{ color: '#E97472', textAlign: 'justify', paddingRight: '10px' }}>
                        {(doc.status === Enum.EDocumentStatus.Refused ||
                          doc.status === Enum.EDocumentStatus.Incomplete) &&
                          doc.refusalReason}
                      </span>
                    </td>
                  ) : (
                    doc.refusalReason !== null && (
                      <td data-label={t('uploadRequiredFile.note')}>
                        <span style={{ color: '#E97472', textAlign: 'justify', padding: '0  10px 0 5px ' }}>
                          {(doc.status === Enum.EDocumentStatus.Refused ||
                            doc.status === Enum.EDocumentStatus.Incomplete) &&
                            doc.refusalReason}
                        </span>
                      </td>
                    )
                  ))}
                <td className="fixed-width-250" data-label={t<string>('uploadRequiredFile.status')}>
                  <span className={getStatusClass(doc.status)}>{t<string>(`enum.eDocumentStatus.${doc.status}`)}</span>
                </td>
                <td data-label={t('uploadRequiredFile.upload')}>
                  <label className="btn-not-resizable" htmlFor={doc.id}>
                    {getUploadButton(doc.status, doc.id)}
                    {Enum.EDocumentStatus.Approved !== doc.status && (
                      <input
                        id={doc.id}
                        style={{ display: 'none' }}
                        type="file"
                        accept={Constants.SupportedDocumentTypes}
                        onChange={(e) => onChangeHandler(e, doc)}
                        disabled={doc.status === Enum.EDecision.Approved}
                        multiple
                      />
                    )}
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
export default RequiredDocumentComponent
